import React, { useState, useEffect, useMemo } from "react";
import {
  BrainCircuit,
  Users,
  PartyPopper,
  Coffee,
  Wrench,
  Zap,
  Laptop,
  Clock,
  Presentation,
  Trophy,
  X,
  Lock,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Settings,
  ArrowLeft,
  ArrowRight,
  Slack,
  Calendar,
  BookUser,
  Target,
  UsersRound,
  FileText,
  Rocket,
  LogIn,
  LogOut,
  UserCheck,
  Shield,
  GraduationCap,
  Send,
} from "lucide-react";

// Импорты Firebase
import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  setLogLevel,
  collection,
  query,
  getDocs,
  updateDoc,
} from "firebase/firestore";

// --- Data for Stages and Shared Pages ---
// (Данные этапов и общих страниц остаются без изменений)
// ВАЖНО: Мы изменим Этап 8, чтобы использовать React-форму вместо EmbeddedForm

// Компонент для Этапа 8
function SubmissionForm({ db, appId, userId }) {
  const [teamName, setTeamName] = useState("");
  const [description, setDescription] = useState("");
  const [demoLink, setDemoLink] = useState("");
  const [repoLink, setRepoLink] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submissionRef = doc(
    db,
    `/artifacts/${appId}/public/data/submissions`,
    userId
  );

  // Проверяем, сдавал ли пользователь уже проект
  useEffect(() => {
    const checkSubmission = async () => {
      const docSnap = await getDoc(submissionRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setTeamName(data.teamName);
        setDescription(data.description);
        setDemoLink(data.demoLink);
        setRepoLink(data.repoLink);
        setIsSubmitted(true);
      }
    };
    checkSubmission();
  }, [submissionRef]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teamName || !description || !demoLink) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      await setDoc(
        submissionRef,
        {
          teamName,
          description,
          demoLink,
          repoLink,
          submittedAt: new Date(),
          userId: userId,
        },
        { merge: true }
      );
      setIsSubmitted(true);
    } catch (err) {
      console.error("Error submitting project:", err);
      setError("Failed to submit project. Please try again.");
    }
    setLoading(false);
  };

  if (isSubmitted) {
    return (
      <div className="p-4 bg-green-100 border border-green-400 rounded-lg text-green-800">
        <h4 className="font-semibold text-lg mb-2">✅ Project Submitted!</h4>
        <p>
          Your project "<strong>{teamName}</strong>" has been received. You can
          still make changes and re-submit until the deadline.
        </p>
        <Button
          text="Edit Submission"
          onClick={() => setIsSubmitted(false)}
          className="mt-4"
          variant="secondary"
        />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="p-4 bg-gray-100 border border-gray-300 rounded-lg space-y-4"
    >
      <h4 className="font-semibold text-lg text-gray-900">
        ⏰ Final Submission
      </h4>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Team Name *
        </label>
        <input
          type="text"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md"
          placeholder="Our Awesome Team"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Project Description *
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md"
          rows={3}
          placeholder="What does your project do?"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Demo Video Link *
        </label>
        <input
          type="url"
          value={demoLink}
          onChange={(e) => setDemoLink(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md"
          placeholder="https://loom.com/..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Repo/Project Link (optional)
        </label>
        <input
          type="url"
          value={repoLink}
          onChange={(e) => setRepoLink(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md"
          placeholder="https://github.com/..."
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button
        text={loading ? "Submitting..." : "Submit Project"}
        type="submit"
        icon={Send}
        disabled={loading}
      />
    </form>
  );
}

const stagesData = [
  {
    num: 1,
    title: "Team Registration",
    time: "Open until Sunday 8:30 AM PST",
    icon: Users,
    content: (
      <>
        <p className="mb-4">
          Before we start, please register your team. Solo participants are
          welcome — just enter your own details.
        </p>
        <EmbeddedForm
          title="📝 Team Registration Form"
          fields={[
            "Team Name",
            "Track (Choose one)",
            "Product Idea (optional)",
            "Member 1-4: Full Name, Email, LinkedIn",
          ]}
          formLink="[REG_FORM_LINK]"
        />
        <p className="mt-4">
          After submitting:
          <br />
          → Join Slack and introduce your team in #team-formation.
          <br />→ If solo, write a short intro to find teammates.
        </p>
        <div className="flex gap-2 mt-4">
          <Button href="#" text="Join Slack" icon={Slack} />
          <Button href="#" text="Open #team-formation" />
        </div>
      </>
    ),
  },
  {
    num: 2,
    title: "Opening Ceremony & Challenge Reveal",
    time: "Sun 9:00–9:15 AM PST",
    icon: PartyPopper,
    content: (
      <>
        <p className="mb-4">
          Watch the livestream for a short welcome, event overview, and the
          official challenge reveal.
        </p>
        <EmbeddedVideo title="🎬 Livestream" />
        <div className="mt-4 p-4 bg-gray-100 rounded-lg">
          <h4 className="font-semibold text-lg mb-2 text-gray-900">
            📄 Challenge Prompt
          </h4>
          <p className="text-gray-700">Full details & resources:</p>
          <Button
            href="#"
            text="View Challenge Details"
            icon={FileText}
            className="mt-2"
          />
        </div>
      </>
    ),
  },
  {
    num: 3,
    title: "Build Session #1",
    time: "9:15–12:30 PM PST",
    icon: BrainCircuit,
    content: (
      <>
        <p className="mb-4">
          Goal: frame the problem, define use case, and create MVP.
        </p>
        <h4 className="font-semibold text-gray-900 mb-2">Suggested flow:</h4>
        <ul className="list-disc list-inside space-y-1 mb-4 text-gray-700">
          <li>9:15–10:00 — Problem framing</li>
          <li>10:00–11:00 — Architecture / UX sketch</li>
          <li>11:00–12:30 — First working demo</li>
        </ul>
        <h4 className="font-semibold text-gray-900 mb-2">Resources:</h4>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button href="#" text="MVP in 2h guide" variant="secondary" />
          <Button href="#" text="Pitch Template" variant="secondary" />
          <Button href="#" text="Demo Video Tips" variant="secondary" />
        </div>
        <p className="mt-4 font-semibold text-gray-800">
          Need help? → Slack #mentors-help
        </p>
      </>
    ),
  },
  {
    num: 4,
    title: "Break",
    time: "12:30–1:00 PM PST",
    icon: Coffee,
    content: (
      <>
        <p className="mb-4">Take a short break and relax.</p>
        <Button
          href="#"
          text="Optional: Join Random Coffee"
          icon={Coffee}
          variant="secondary"
        />
      </>
    ),
  },
  {
    num: 5,
    title: "Build Session #2 & Mentor Hours 1",
    time: "1:00–3:00 PM PST",
    icon: Wrench,
    content: (
      <>
        <p className="mb-4">
          Goal: Improve UX, fix bugs, and prepare pitch deck.
        </p>
        <h4 className="font-semibold text-gray-900 mb-2">Mentor Hours:</h4>
        <div className="p-4 bg-gray-100 rounded-lg mb-4 border border-gray-300">
          <h5 className="font-semibold mb-2 text-gray-900">View Calendar</h5>
          <div className="w-full h-40 bg-gray-200 rounded-md flex items-center justify-center text-gray-500">
            [MENTOR_CALENDAR_EMBED]
          </div>
        </div>
        <Button href="#" text="Book a Slot" icon={Calendar} />
        <p className="mt-4">Bring: 1 slide, demo link, and 2 key questions.</p>
      </>
    ),
  },
  {
    num: 6,
    title: "Mid Check-In",
    time: "3:00–3:15 PM PST",
    icon: Zap,
    content: (
      <>
        <p className="mb-4">Submit a 1-line update:</p>
        <ul className="list-disc list-inside space-y-1 mb-4 text-gray-700">
          <li>What you shipped</li>
          <li>Any blockers</li>
        </ul>
        <EmbeddedForm
          title="⚡ Mid Check-In"
          fields={["Update", "Blockers"]}
          formLink="[CHECKIN_FORM_LINK]"
        />
      </>
    ),
  },
  {
    num: 7,
    title: "Build Session #3 & Mentor Hours 2",
    time: "3:15–5:00 PM PST",
    icon: Laptop,
    content: (
      <>
        <p className="mb-4">Final push: record demo video and finalize deck.</p>
        <h4 className="font-semibold text-gray-900 mb-2">Checklist:</h4>
        <ul className="list-inside space-y-1 mb-4 text-gray-700">
          <li className="flex items-center gap-2">
            <div className="w-4 h-4 border border-gray-400 rounded-sm bg-white"></div>{" "}
            Demo Video (≤2 min)
          </li>
          <li className="flex items-center gap-2">
            <div className="w-4 h-4 border border-gray-400 rounded-sm bg-white"></div>{" "}
            Pitch Deck (5 slides)
          </li>
          <li className="flex items-center gap-2">
            <div className="w-4 h-4 border border-gray-400 rounded-sm bg-white"></div>{" "}
            Repo/Project Link
          </li>
          <li className="flex items-center gap-2">
            <div className="w-4 h-4 border border-gray-400 rounded-sm bg-white"></div>{" "}
            Short description
          </li>
          <li className="flex items-center gap-2">
            <div className="w-4 h-4 border border-gray-400 rounded-sm bg-white"></div>{" "}
            Team members
          </li>
        </ul>
        <Button
          href="#"
          text="Mentor Room"
          icon={UsersRound}
          variant="secondary"
        />
      </>
    ),
  },
  {
    num: 8,
    title: "Submission Deadline",
    time: "5:00–5:30 PM PST",
    icon: Clock,
    // Контент теперь будет функцией, принимающей db, appId, userId
    content: ({ db, appId, userId }) => (
      <>
        <p className="mb-4 font-semibold text-lg text-orange-600">
          Deadline: 5:30 PM PST
        </p>
        <p className="mb-4">
          Submit your project directly below. You can update until the deadline.
        </p>
        <SubmissionForm db={db} appId={appId} userId={userId} />
      </>
    ),
  },
  {
    num: 9,
    title: "Demo Session & Technical Review",
    time: "5:30–7:00 PM PST",
    icon: Presentation,
    content: (
      <>
        <p className="mb-4">Each team: 3-min pitch + 2-min Q&A.</p>
        <p className="mb-4">Judges review submissions live.</p>
        <EmbeddedVideo title="🎤 Demo Stream" />
        <div className="mt-4">
          <Button
            href="#"
            text="View Running Order"
            icon={FileText}
            variant="secondary"
          />
        </div>
      </>
    ),
  },
  {
    num: 10,
    title: "Awards & Closing Vibe",
    time: "7:00–7:15 PM PST",
    icon: Trophy,
    content: (
      <>
        <p className="mb-4">Join the live announcement of winners!</p>
        <EmbeddedVideo title="🏆 Awards Stream" />
        <div className="mt-4">
          <Button
            href="#"
            text="See Winners List"
            icon={Trophy}
            variant="secondary"
          />
        </div>
        <p className="mt-6 text-lg font-semibold text-gray-900">
          Thank you for building with us at AI4HACK!
        </p>
      </>
    ),
  },
];

const sharedPages = {
  tracks: {
    title: "Tracks Overview",
    icon: Target,
    content: (
      <ul className="space-y-3">
        <li>
          <strong className="text-orange-600">
            1) Autonomous AI Agents for Work & Life
          </strong>
        </li>
        <li>
          <strong className="text-orange-600">
            2) Generative AI for Real-World Creation
          </strong>
        </li>
        <li>
          <strong className="text-orange-600">
            3) Personal Intelligence & Decision Support
          </strong>
        </li>
        <li>
          <strong className="text-orange-600">
            4) AI for the Next Billion Users
          </strong>
        </li>
      </ul>
    ),
  },
  resources: {
    title: "Resources",
    icon: Rocket,
    content: (
      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">APIs:</h3>
          <p>OpenAI, Hugging Face, Replit</p>
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Datasets:</h3>
          <p>Kaggle, Google Dataset Search</p>
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Tools:</h3>
          <p>Canva, Gamma, Loom, Notion</p>
        </div>
      </div>
    ),
  },
  mentors: {
    title: "Mentors",
    icon: UsersRound,
    content: (
      <>
        <h3 className="font-semibold text-gray-900 mb-3">Mentor Directory:</h3>
        <ul className="space-y-2 mb-4">
          <li>
            <strong>[Name]</strong> — [Company] — [LinkedIn]
          </li>
          <li>
            <strong>[Name]</strong> — [Company] — [LinkedIn]
          </li>
          <li>
            <strong>[Name]</strong> — [Company] — [LinkedIn]
          </li>
        </ul>
        <Button href="#" text="Book Slots" icon={Calendar} />
      </>
    ),
  },
};

// --- Reusable Components ---

// Button Component
function Button({
  href,
  text,
  icon: Icon,
  className = "",
  variant = "primary",
  ...props
}) {
  const baseStyle =
    "inline-flex items-center gap-2 font-semibold py-2 px-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed";

  const variants = {
    primary: "bg-orange-600 text-white hover:bg-orange-700",
    secondary:
      "bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300",
  };

  const AsComponent = href ? "a" : "button";

  return (
    <AsComponent
      href={href}
      target={href ? "_blank" : undefined}
      rel={href ? "noopener noreferrer" : undefined}
      className={`${baseStyle} ${variants[variant]} ${className}`}
      {...props}
    >
      {Icon && <Icon size={18} />}
      {text}
    </AsComponent>
  );
}

// Placeholder for Form Embed
function EmbeddedForm({ title, fields, formLink }) {
  return (
    <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg">
      <h4 className="font-semibold text-lg mb-3 text-gray-900">{title}</h4>
      <div className="space-y-2 mb-4">
        {fields.map((field) => (
          <div
            key={field}
            className="text-sm p-3 bg-white border border-gray-300 rounded-md text-gray-700"
          >
            {field}
          </div>
        ))}
      </div>
      <Button href={formLink} text="Open Form" />
    </div>
  );
}

// Placeholder for Video Embed
function EmbeddedVideo({ title }) {
  return (
    <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg">
      <h4 className="font-semibold text-lg mb-3 text-gray-900">{title}</h4>
      <div className="aspect-video w-full bg-gray-200 rounded-md flex items-center justify-center text-gray-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="5 3 19 12 5 21 5 3"></polygon>
        </svg>
        <p className="ml-2">[Video Embed]</p>
      </div>
    </div>
  );
}

// Header Component
function AppHeader({ onShowModal, user, userRole, onSignOut }) {
  const roleDisplay = {
    admin: { icon: Shield, name: "Admin", color: "text-red-600" },
    mentor: { icon: GraduationCap, name: "Mentor", color: "text-blue-600" },
    participant: {
      icon: UserCheck,
      name: "Participant",
      color: "text-gray-600",
    },
  };

  const currentRole = roleDisplay[userRole] || roleDisplay.participant;
  const RoleIcon = currentRole.icon;

  return (
    <header className="w-full p-4 md:p-6 bg-white/80 backdrop-blur-md border-b border-gray-200/70 sticky top-0 z-20">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-center">
        <div className="text-center sm:text-left">
          <h1 className="text-2xl font-bold text-gray-900">
            <span className="text-orange-600">🧠 AI4HACK</span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">Hackathon AI for Hack</p>
        </div>
        <nav className="flex flex-wrap justify-center sm:justify-end items-center gap-2 mt-4 sm:mt-0">
          {userRole === "participant" && (
            <>
              <Button href="#" text="Slack" icon={Slack} variant="secondary" />
              <Button
                href="#"
                text="Schedule"
                icon={Calendar}
                variant="secondary"
              />
              <Button
                href="#"
                text="Tracks"
                icon={Target}
                variant="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  onShowModal("tracks");
                }}
              />
              <Button
                href="#"
                text="Resources"
                icon={Rocket}
                variant="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  onShowModal("resources");
                }}
              />
              <Button
                href="#"
                text="Mentors"
                icon={BookUser}
                variant="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  onShowModal("mentors");
                }}
              />
            </>
          )}
          {user && (
            <div className="flex items-center gap-4 pl-4">
              <div className="flex items-center gap-2">
                <RoleIcon size={18} className={currentRole.color} />
                <span className={`text-sm font-semibold ${currentRole.color}`}>
                  {currentRole.name}
                </span>
              </div>
              <Button
                text="Sign Out"
                icon={LogOut}
                variant="secondary"
                onClick={onSignOut}
              />
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}

// Stage Card Component
function StageCard({ stage, currentStageIndex, db, appId, userId }) {
  const [isOpen, setIsOpen] = useState(false);

  const status = useMemo(() => {
    if (stage.num < currentStageIndex + 1) return "completed";
    if (stage.num === currentStageIndex + 1) return "open";
    return "locked";
  }, [stage.num, currentStageIndex]);

  const Icon = stage.icon;

  // Auto-open when a stage becomes active
  useEffect(() => {
    setIsOpen(status === "open");
  }, [status]);

  const statusConfig = {
    locked: {
      Icon: Lock,
      bgColor: "bg-white/50",
      borderColor: "border-gray-300/70",
      textColor: "text-gray-500",
      accentColor: "text-gray-500",
      label: "LOCKED",
    },
    open: {
      Icon: ChevronDown,
      bgColor: "bg-white",
      borderColor: "border-orange-600",
      textColor: "text-gray-900",
      accentColor: "text-orange-600",
      label: "OPEN",
    },
    completed: {
      Icon: CheckCircle,
      bgColor: "bg-white/70",
      borderColor: "border-green-500/50",
      textColor: "text-gray-600",
      accentColor: "text-green-500",
      label: "COMPLETED",
    },
  };

  const config = statusConfig[status];

  const handleToggle = () => {
    if (status !== "locked") {
      setIsOpen(!isOpen);
    }
  };

  // Если контент - функция, вызываем ее с нужными props
  const contentNode =
    typeof stage.content === "function"
      ? stage.content({ db, appId, userId })
      : stage.content;

  return (
    <div
      className={`relative pl-12 pr-6 py-5 rounded-lg border-l-4 transition-all duration-300 ${config.borderColor} ${config.bgColor} backdrop-blur-sm shadow-md`}
    >
      {/* Icon on the timeline */}
      <div
        className={`absolute -left-5 top-5 w-10 h-10 rounded-full ${
          status === "open" ? "bg-orange-600" : "bg-gray-300"
        } ${
          status === "completed" ? "bg-green-500" : ""
        } flex items-center justify-center ring-4 ring-gray-50`}
      >
        <Icon
          size={20}
          className={
            status === "open" || status === "completed"
              ? "text-white"
              : "text-gray-500"
          }
        />
      </div>

      {/* Card Header */}
      <div
        className={`flex justify-between items-center ${
          status !== "locked" ? "cursor-pointer" : "cursor-default"
        }`}
        onClick={handleToggle}
      >
        <div>
          <span className={`text-xs font-bold uppercase ${config.accentColor}`}>
            {config.label}
          </span>
          <h3 className={`text-xl font-semibold ${config.textColor}`}>
            {stage.num}. {stage.title}
          </h3>
          <p
            className={`text-sm ${
              status === "locked" ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {stage.time}
          </p>
        </div>
        {status !== "locked" && (
          <div className={`p-2 rounded-full ${config.accentColor}`}>
            {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>
        )}
      </div>

      {/* Card Content (Collapsible) */}
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="mt-4 pt-4 border-t border-gray-200/70 text-gray-700">
          {contentNode}
        </div>
      </div>
    </div>
  );
}

// Modal for Shared Pages
function SharedModal({ modalType, onClose }) {
  if (!modalType) return null;

  const page = sharedPages[modalType];
  const Icon = page.icon;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-md z-40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-gray-300 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white/90 backdrop-blur-sm z-10">
          <div className="flex items-center gap-3">
            <Icon className="text-orange-600" size={24} />
            <h2 className="text-2xl font-semibold text-gray-900">
              {page.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 transition-colors p-2 rounded-full hover:bg-gray-100"
          >
            <X size={24} />
          </button>
        </div>
        <div className="p-6 text-gray-700">{page.content}</div>
      </div>
    </div>
  );
}

// Admin Controls (for REAL Firestore control)
function AdminControls({ current, total, onStageChange }) {
  const [isOpen, setIsOpen] = useState(true); // Открыт по умолчанию для админа

  const handlePrev = () => {
    const newStage = Math.max(0, current - 1);
    onStageChange(newStage);
  };

  const handleNext = () => {
    const newStage = Math.min(total - 1, current + 1);
    onStageChange(newStage);
  };

  return (
    <div className="relative z-10">
      <div className="bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-full">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-gray-900">Stage Controls</h4>
          <span className="text-gray-600 font-semibold">
            <span className="font-bold text-orange-600">{current + 1}</span> /{" "}
            {total}
          </span>
        </div>
        <div className="flex justify-between">
          <button
            onClick={handlePrev}
            disabled={current === 0}
            className="flex items-center gap-2 bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={18} /> Prev
          </button>
          <button
            onClick={handleNext}
            disabled={current === total - 1}
            className="flex items-center gap-2 bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Dashboards for Roles ---

// 1. Участник
function ParticipantDashboard({
  currentStageIndex,
  modalContent,
  onShowModal,
  db,
  appId,
  userId,
}) {
  return (
    <>
      <main className="max-w-3xl mx-auto p-4 md:p-8">
        <div className="relative flex flex-col gap-6">
          {/* Timeline connecting line */}
          <div className="absolute left-4 top-5 bottom-5 w-1.5 bg-gray-300 rounded-full" />

          {stagesData.map((stage) => (
            <StageCard
              key={stage.num}
              stage={stage}
              currentStageIndex={currentStageIndex}
              db={db}
              appId={appId}
              userId={userId}
            />
          ))}
        </div>
      </main>
      <SharedModal modalType={modalContent} onClose={() => onShowModal(null)} />
    </>
  );
}

// 2. Ментор
function MentorDashboard({ db, appId, userId }) {
  // TODO: Загрузить проекты для оценки
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8">
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-300">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Mentor Dashboard
        </h2>
        <p className="text-gray-700">
          Welcome, Mentor! This is where you will manage your schedule and
          review project submissions. (Functionality coming soon)
        </p>
      </div>
    </div>
  );
}

// 3. Администратор
function AdminDashboard({ current, total, onStageChange, db, appId }) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      const usersQuery = query(
        collection(db, `/artifacts/${appId}/public/data/users`)
      );
      const querySnapshot = await getDocs(usersQuery);
      const userList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(userList);
    };
    fetchUsers();
  }, [db, appId]);

  const updateUserRole = async (id, newRole) => {
    const userDocRef = doc(db, `/artifacts/${appId}/public/data/users`, id);
    await updateDoc(userDocRef, { role: newRole });
    // Обновляем локальное состояние
    setUsers(users.map((u) => (u.id === id ? { ...u, role: newRole } : u)));
  };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6">
      <AdminControls
        current={current}
        total={total}
        onStageChange={onStageChange}
      />

      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-300">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          User Management
        </h2>
        <div className="space-y-3">
          {users.map((user) => (
            <div
              key={user.id}
              className="flex justify-between items-center p-3 bg-gray-50 rounded-md border"
            >
              <span className="text-gray-700" title={user.id}>
                {user.email}
              </span>
              <select
                value={user.role}
                onChange={(e) => updateUserRole(user.id, e.target.value)}
                className="p-2 border border-gray-300 rounded-md"
              >
                <option value="participant">Participant</option>
                <option value="mentor">Mentor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Экран входа
function LoginScreen({ onSignIn }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center p-10 bg-white shadow-xl rounded-lg border border-gray-300">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Welcome to <span className="text-orange-600">AI4HACK</span>
        </h1>
        <p className="text-gray-600 mb-6">
          Please sign in to access the workspace.
        </p>
        <Button text="Sign in with Google" icon={LogIn} onClick={onSignIn} />
      </div>
    </div>
  );
}

// --- Main App Component (Роутер) ---
export default function App() {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [modalContent, setModalContent] = useState(null);

  // Состояние Firebase
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState("participant"); // По умолчанию
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [appId, setAppId] = useState("default-app-id");

  // 1. Инициализация Firebase
  useEffect(() => {
    // @ts-ignore
    const firebaseConfig =
      typeof __firebase_config !== "undefined"
        ? JSON.parse(__firebase_config)
        : undefined;
    // @ts-ignore
    const id = typeof __app_id !== "undefined" ? __app_id : "default-app-id";
    setAppId(id);

    if (firebaseConfig) {
      try {
        const app = initializeApp(firebaseConfig);
        const firestoreDb = getFirestore(app);
        const firebaseAuth = getAuth(app);

        setDb(firestoreDb);
        setAuth(firebaseAuth);
        setLogLevel("Debug");

        // Слушатель состояния аутентификации
        onAuthStateChanged(firebaseAuth, async (user) => {
          if (user) {
            // Пользователь вошел
            setUser(user);
            // Проверяем/создаем роль пользователя
            const userDocRef = doc(
              firestoreDb,
              `/artifacts/${id}/public/data/users`,
              user.uid
            );
            const docSnap = await getDoc(userDocRef);

            if (docSnap.exists()) {
              setUserRole(docSnap.data().role || "participant");
            } else {
              // Создаем нового пользователя с ролью по умолчанию
              await setDoc(userDocRef, {
                email: user.email,
                displayName: user.displayName,
                role: "participant",
                createdAt: new Date(),
              });
              setUserRole("participant");
            }
          } else {
            // Пользователь не вошел
            setUser(null);
            setUserRole("participant");
          }
          setIsAuthReady(true);
        });
      } catch (e) {
        console.error("Firebase init error", e);
      }
    }
  }, []);

  // 2. Подписка на состояние хакатона (этапы)
  useEffect(() => {
    if (!db || !isAuthReady) return;

    const stateDocRef = doc(
      db,
      `/artifacts/${appId}/public/data/hackathonState`,
      "main"
    );

    const unsubscribe = onSnapshot(
      stateDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setCurrentStageIndex(docSnap.data().currentStage || 0);
        } else {
          // Инициализируем, если нет
          setDoc(stateDocRef, { currentStage: 0 }, { merge: true });
          setCurrentStageIndex(0);
        }
      },
      (error) => {
        console.error("Error listening to state:", error);
      }
    );

    return () => unsubscribe();
  }, [db, isAuthReady, appId]);

  // Функция для обновления этапа в Firestore (для админа)
  const handleStageChange = async (newStageIndex) => {
    if (!db) return;
    const stateDocRef = doc(
      db,
      `/artifacts/${appId}/public/data/hackathonState`,
      "main"
    );
    try {
      await setDoc(stateDocRef, { currentStage: newStageIndex });
    } catch (e) {
      console.error("Error updating stage:", e);
    }
  };

  // Функция входа
  const handleSignIn = async () => {
    if (!auth) return;
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google:", error);
    }
  };

  // Функция выхода
  const handleSignOut = async () => {
    if (!auth) return;
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // --- Рендеринг ---

  const renderDashboard = () => {
    switch (userRole) {
      case "admin":
        return (
          <AdminDashboard
            current={currentStageIndex}
            total={stagesData.length}
            onStageChange={handleStageChange}
            db={db}
            appId={appId}
          />
        );
      case "mentor":
        return <MentorDashboard db={db} appId={appId} userId={user.uid} />;
      case "participant":
      default:
        return (
          <ParticipantDashboard
            currentStageIndex={currentStageIndex}
            modalContent={modalContent}
            onShowModal={setModalContent}
            db={db}
            appId={appId}
            userId={user.uid}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 font-['Inter']">
      <AppHeader
        onShowModal={setModalContent}
        user={user}
        userRole={userRole}
        onSignOut={handleSignOut}
      />

      {!isAuthReady && <div className="text-center p-10">Загрузка...</div>}

      {isAuthReady && !user && <LoginScreen onSignIn={handleSignIn} />}

      {isAuthReady && user && renderDashboard()}
    </div>
  );
}
